import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { differenceInDays, differenceInHours, differenceInMinutes, differenceInMonths, differenceInYears } from 'date-fns';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, map, Observable, of, publishReplay, refCount, retry, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { assetUrl } from '../asset-url';
import { SnackbarComponent } from '../components/snackbar/snackbar.component';
import { NotificationSettings } from '../interfaces/notification-settings';
import { AddNewComment, AddNewPost, EditComment } from '../interfaces/post.interface';
import { PostsSpace, SortOrderDetail, WeavyData } from '../interfaces/space.interface';
import { SocketService } from './socket.service';
@Injectable({
  providedIn: 'root'
})
export class CollaborationService {
  api_host_url = environment.api_url;
  exp_url = environment.exp_url;
  referral_api_url = environment.referral_api_url;
  duplicateAPIObservable: any = {};
  allPostSpaceDetails!: PostsSpace;
  allDocumentsList: any = [];
  spaceId: any;
  errorMessage = 'Error while retrieving the data for this request. Please try after some time.';
  postsWeavyData: any;
  token: string = '';
  apimToken: string = '';
  decryptToken: any = '';
  notificationSettings!: NotificationSettings;
  sharedNotificationSettings!: NotificationSettings;
  public showOrHideSideNav: BehaviorSubject<{ [key: string]: boolean }> = new BehaviorSubject({});
  public showOrHideSideSharedNav: BehaviorSubject<{ [key: string]: boolean }> = new BehaviorSubject({});
  referralDocumentsSortingDetails: SortOrderDetail = {
    filedName: 'fileName',
    sortOrder: 'asc'
  }
  policyDocumentsSortingDetails: SortOrderDetail = {
    filedName: 'fileName',
    sortOrder: 'asc'
  }
  maximizeFlag: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  profileAccessToken: any;
  profilePictures: any = {};
  editorDataModified: any = {};
  onMessageSuccessEvent: Subject<{ id: string, success: boolean }> = new Subject();
  clearRTEData: Subject<boolean> = new Subject();
  isApplicationRunningOnOnItSystem = false;
  sasTokenDetails: any;
  showPostLoader: boolean = false;
  userEmail = '';
  isMobileScreen = false;
  additionalDataPulledInTabs = {};
  constructor(
    private http: HttpClient,
    private socketService: SocketService,
    private snackBar: MatSnackBar
  ) { }


  getShowOrHideSideNav() {
    return this.showOrHideSideNav.value;
  }

  setShowOrHideSideNav(drawerKey: string, drawerValue: boolean) {
    this.showOrHideSideNav.next({ ...this.showOrHideSideNav.value, ...{ [drawerKey]: drawerValue } });
  }

  getShowOrHideSideSharedNav() {
    return this.showOrHideSideSharedNav.value;
  }

  setShowOrHideSideSharedNav(drawerKey: string, drawerValue: boolean) {
    this.showOrHideSideSharedNav.next({ ...this.showOrHideSideSharedNav.value, ...{ [drawerKey]: drawerValue } });
  }

  uploadFile(reqData: any) {
    return this.http.post(`${this.api_host_url}/documents/space/${this.socketService.spaceId}/upload`,
      reqData
    );
  }
  getUsersList(searchText: string) {
    return this.http.get(`${this.api_host_url}/atMentionUsers?size=10&text=${searchText}`).pipe(map((res: any) => {
      res.data = res.data.map((item: any, i: number) => {
        return { ...item, id: `${item.id}--${item.email}`, value: `${item.userName}`, "label": `${item.name} (${item.email})` };
      })
      return res.data
    }));
  }

  async getBase64File(file: any): Promise<any> {
    return new Promise(async (resolve, reject) => {
      const reader = new FileReader();
      await reader.readAsDataURL(file);
      reader.onload = () => {
        const result: any = reader.result;
        if (result) {
          const stringAfterBase64 = result.split('base64,')[1];
          resolve(stringAfterBase64 ?? result);
        } else {
          reject(new Error("Failed to read file."));
        }
      };
      reader.onerror = reject;
    });
  }

  downloadPosts() {
    const options = {
      responseType: 'blob' as 'json',
    }
    return this.http.get(`${this.api_host_url}/spaces/${this.allPostSpaceDetails.spaceId}/apps/${this.allPostSpaceDetails.appId}/downloadPosts`, options)
  }

  doDownload(name: string, type: string, data: any) {
    const downloadLink = document.createElement('a');
    downloadLink.href = window.URL.createObjectURL(new Blob([data], { type: type }));
    downloadLink.target = '_blank';

    const fileExtension = type.split('/')[1];
    const fileName = `${name}.${fileExtension}`;
    downloadLink.download = fileName;

    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }

  getSpaceAndAppDetails(payload: any): Observable<PostsSpace[]> {
    const url = `${this.api_host_url}/initialize-posts`;
    const uniqueKey = `${payload.spaceKey}/${payload.spaceName}/${payload.userEmail}`;
    if (!this.duplicateAPIObservable[uniqueKey]) {
      this.duplicateAPIObservable[uniqueKey] = this.http.post(url, payload)
        .pipe(publishReplay(), refCount()).pipe(map((response: any) => response?.data));
    }
    return this.duplicateAPIObservable[uniqueKey];
  }

  initializePosts(payload: any, isRefresh = false): Observable<PostsSpace> {
    if (this.additionalDataPulledInTabs[payload.appKey] && !isRefresh) {
      const res = cloneDeep(this.additionalDataPulledInTabs[payload.appKey]);
      return of(res);
    }
    return this.http.post(`${this.api_host_url}/initialize-posts`, payload).pipe(retry(1), map((response: any) => response?.data));
  }

  displayErrorMessage(error: any, errorMessage = this.errorMessage, panelClass = 'error-snackbar', duration = 2000, type = '') {
    let message = errorMessage;
    if (error) {
      switch (error.status) {
        case 401:
          message = "You are not authorized to perform this actions. Please get a access to use this platform.";
          localStorage.removeItem("tokenFromUrl");
          break;
        default:
          message = errorMessage;

      }
    }
    this.snackBar.openFromComponent(SnackbarComponent, {
      panelClass,
      duration,
      data: {
        snackBar: this.snackBar,
        message,
        type
      },
    });
  }

  displaySuccessMessage(msg: string) {
    this.snackBar.openFromComponent(SnackbarComponent, {
      panelClass: 'success-snackbar',
      duration: 2000,
      data: {
        snackBar: this.snackBar,
        message: msg,
      },
    });
  }

  initDocuments(payload: any, isRefresh = false) {
    if (this.additionalDataPulledInTabs[payload.appKey] && !isRefresh) {
      const res = cloneDeep(this.additionalDataPulledInTabs[payload.appKey]);
      return of(res);
    }
    return this.http.post(`${this.api_host_url}/initialize-documents`, payload).pipe(retry(1), map((response: any) => response?.data));
  }

  downloadDocument(blobId: any, reqBody: any, urlString: string) {
    const options = {
      responseType: 'blob' as 'json'
    };
    return this.http.post(`${this.api_host_url}/blobs/${blobId}/${urlString}`,
      reqBody, options);
  }

  addPost(post: AddNewPost) {
    return this.http.post(`${this.api_host_url}/add-post`, post);
  }

  addComment(comment: AddNewComment) {
    return this.http.post(`${this.api_host_url}/add-comment`, comment);
  }

  createDocument(spaceID: any, appID: any, requestBody: any, fileType: string = 'File') {
    return this.http.post(`${this.api_host_url}/spaces/${spaceID}/apps/${appID}/create${fileType}`, requestBody).pipe(map((response: any) => response?.data));
  }

  updateDocument(spaceID: any, appID: any, requestBody: any) {
    return this.http.post(`${this.api_host_url}/spaces/${spaceID}/apps/${appID}/updateFile`, requestBody).pipe(map((response: any) => response?.data));
  }

  likeOrFollow(requestBody: any, actionType: string = 'like') {
    return this.http.post(`${this.api_host_url}/user/actions/${actionType}`, requestBody);
  }

  updatePostSpaceLevelSubscription(requestBody: any) {
    return this.http.post(`${this.api_host_url}/spaceMemberUpdate`, requestBody);
  }

  fileAction(payload: any, actionType: string) {
    return this.http.post(`${this.api_host_url}/spaces/${payload.spaceId}/apps/${payload.appId}/${actionType}`, payload).pipe(map((response: any) => response?.data));
  }

  editPost(post: AddNewPost) {
    return this.http.post(`${this.api_host_url}/update-post`, post);
  }

  editComment(comment: EditComment) {
    return this.http.post(`${this.api_host_url}/update-comment`, comment);
  }

  updateNote(spaceID: any, appID: any, requestBody: any) {
    return this.http.post(`${this.api_host_url}/spaces/${spaceID}/apps/${appID}/updateNote`, requestBody).pipe(map((response: any) => response?.data));
  }

  getWeavyOnItData(referralId: any) {
    return this.http.get(`${this.referral_api_url}/synergy-onit-data/${referralId}`);
  }

  getSummaryData(type: string, email: string, searchText: string = '') {
    let payload: any = {
      email: email
    }
    if (searchText) {
      payload.searchText = searchText
    }
    return this.http.post(`${this.api_host_url}/summary-${type}`, payload).pipe(map((response: any) => response?.data));
  }

  getSummaryDataOnDemand(activeTab: string, email: string, searchText: string = '', size: number, page: number) {
    let payload: any = {
      email,
      activeTab,
      size,
      page,
      searchText
    }
    return this.http.post(`${this.api_host_url}/collaboration-summary`, payload).pipe(map((response: any) => response?.data));
  }

  getToken(): string {
    return this.token;
  }

  getDecryptToken() {
    if (this.decryptToken) {
      return this.decryptToken;
    }
    const token = this.getToken();
    try {
      this.decryptToken = JSON.parse(window.atob(token.split('.')[1]));
    } catch {
      this.decryptToken = {};
    }
    return this.decryptToken;
  }

  getEmail() {
    return this.getDecryptToken().unique_name;
  }

  getUserName() {
    return this.getDecryptToken().name.replace(",", '');
  }

  getNotificationSettings(email: string) {
    return this.http.get(`${this.api_host_url}/users/${email}/notification/settings`);
  }

  updateNotificationSettings(payload: NotificationSettings, emailId: string) {
    return this.http.post(`${this.api_host_url}/users/${emailId}/notification/settings`, { ...payload, emailId });
  }

  getSharedNotificationSettings(config: any) {
    const headers = new HttpHeaders({
      skip: '',
      Authorization: 'Bearer ' + config.token
    });

    return this.http.get(`${this.exp_url}/users/${config.userEmail}/notification/settings`, { headers });
  }

  updateSharedNotificationSettings(config: any, payload: NotificationSettings) {
    const headers = new HttpHeaders({
      skip: '',
      Authorization: 'Bearer ' + config.token
    });
    return this.http.post(`${this.exp_url}/users/${config?.userEmail}/notification/settings`, { ...payload, emailId: config?.userEmail }, { headers });
  }

  filterSearchText(text: string, data: any, key: string) {
    if (!data?.length) return data;
    return data.filter((record: any) => record.contentType === 'FILE' ? record.fileName.includes(text) : record[key].includes(text));
  }

  filterSearchTextForAlerts(text: string, data: any, key: string) {
    if (!data?.length) return data;
    return data.filter((record: any) => record[key] ? record[key].includes(text) : false);
  }

  getUserProfilePicture(email: string) {
    return of(assetUrl('images/user.svg'));
    // if (!this.profileAccessToken) {
    //   let profileTokenKey = Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i)).find(key => key.includes('profile'));
    //   let profileTokenItem = JSON.parse(localStorage.getItem(profileTokenKey));
    //   this.profileAccessToken = profileTokenItem.secret;
    // }
    // if (this.profilePictures[email.toLowerCase()]) {
    //   return of(this.profilePictures[email.toLowerCase()]);
    // }
    // const headers = new HttpHeaders({
    //   'X-Profile-Photo-Token': this.profileAccessToken,
    // });
    // return this.http.get(`${this.referral_api_url}exp-collaboration/users/${email}/profile-photo`, { headers, responseType: 'blob' }).pipe(
    //   map((response: any) => {
    //     this.profilePictures[email.toLowerCase()] = URL.createObjectURL(response);
    //     return URL.createObjectURL(response);
    //   }),
    //   catchError(() => {
    //     return of(assetUrl('images/user.svg'));
    //   }));
  }

  getAllDocuments(text?: string) {
    const regex = /:(.+)/;
    const match = this.postsWeavyData.spaceKey.match(regex);

    let tracker = match ? match[1] : null;

    let reqPayload = {
      referralSpaceKey: this.postsWeavyData.spaceKey,
      referralSpaceName: this.postsWeavyData.spaceName,
      policySpaceKey: tracker,
      policySpaceName: this.postsWeavyData.spaceName,
      text: text ?? ""
    }

    return this.http.post(`${this.api_host_url}/atMentionFiles`, reqPayload).pipe(map((response: any) => {
      let data = response?.data?.map((row: any) => {
        row.id = (row.blobId ?? row.id) + '**' + (row.kind?.replace(/ /g, ""));
        return row;
      });
      return data;
    }));
  }

  getActivityId(policyKey: string): Observable<any> {
    const url = `${this.referral_api_url}/referral-document-data/${policyKey}`;
    return this.http.get(url);
  }

  downloadPostsAndPolicyDocsReferral(trackerReferenceNumber: any, payload: any) {
    const url = `${this.api_host_url}/downloadPosts/tracker/${trackerReferenceNumber}`;
    const options = {
      responseType: 'blob' as 'json'
    }
    return this.http.post(url, payload, options)
  }

  downloadPostsAndPolicyDocsIhub(spaceId: string) {
    const url = `${this.api_host_url}/downloadReferral/space/${spaceId}`;
    const options = {
      responseType: 'blob' as 'json'
    }
    return this.http.get(url, options)
  }

  downloadAllFiles(appId: number) {
    const url = `${this.api_host_url}/downloadReferral/app/${appId}`;
    const reqHeaders: any = {
      'Content-type': 'application/json',
      Accept: 'application/json',
      apiVersion: 1
    };
    const options = {
      responseType: 'blob' as 'json',
      headers: new HttpHeaders(reqHeaders)
    }
    return this.http.get(url, options)
  }

  getSasToken() {
    if (this.sasTokenDetails) {
      return of(this.sasTokenDetails);
    }
    return this.http.get(`${this.api_host_url}/sas-token`).pipe(map((response: any) => {
      this.sasTokenDetails = response.data;
      return response.data;
    }));
  }

  getDocumentPreviewURL(eTag: string, fileExtension: string) {
    const headers = new HttpHeaders({
      skip: ''
    });
    const options = {
      responseType: 'blob' as 'json',
      headers
    }
    return this.http.get(`${this.sasTokenDetails.baseUrl}/${eTag}.${fileExtension}?${this.sasTokenDetails.sasToken}`, options);
  }

  getEtagFromBlobId(blobId: any) {
    return this.http.get(`${this.api_host_url}/blobs/${blobId}/e-tag`).pipe(map((response: any) => response.data));
  }

  getAlertsData(email: string, page: number, size: number, searchText: string): Observable<any> {
    const url = `${this.api_host_url}/alerts-by-pagination`;
    const payload: any = {
      email,
      page,
      size,
      searchText
    };
    return this.http.post<any>(url, payload);
  }

  calculateDifferenceBetweenDates(startDate: string | Date, endDate: string | Date) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    let difference;
    let differenceText = '';
    if (startDate && endDate) {
      const years = differenceInYears(end, start);
      const months = differenceInMonths(end, start);
      const days = differenceInDays(end, start);
      const hours = differenceInHours(end, start);
      const minutes = differenceInMinutes(end, start);

      if (years > 0) {
        difference = `${years}`;
        differenceText = "year";
      } else if (months > 0) {
        difference = months;
        differenceText = "month";
      } else if (days > 0) {
        difference = days;
        differenceText = "day";
      } else if (hours > 0) {
        difference = hours;
        differenceText = "hour";
      } else {
        difference = minutes;
        differenceText = "minute";
      }
    }
    difference = difference > 1 ? `${difference} ${differenceText}s` : `${difference} ${differenceText}`;
    return difference;
  }

  deleteVersion(reqBody: any) {
    return this.http.post(`${this.api_host_url}/delete-version`, reqBody).pipe(map((response: any) => response.data));
  }

  getNoteProperties(contentId: any) {
    return this.http.get(`${this.api_host_url}/contents/${contentId}/properties`).pipe(map((response: any) => response.data));
  }

  getAuthenticationTokenFromAPIM() {
    const headers = new HttpHeaders({
      skip: ''
    });
    return this.http.get(`${environment.exp_url}/auth/token`, { headers })
  }

  getObjForInitializeCall(weavyData: WeavyData) {
    const draftTextContains = "~Request#~";
    let { appKey, appName, spaceKey, spaceName, tab, userEmail, draft } = cloneDeep(weavyData);
    if (appName.includes(draftTextContains)) {
      spaceName += draftTextContains + appName.split(draftTextContains)[1]
    }
    return weavyData.draft ? { appKey, appName, spaceKey, spaceName, tab, userEmail, draft } : { appKey, appName, spaceKey, spaceName, tab, userEmail };
  }

  decodeHtmlTagsOnly(html: string): string {
    const decodedHtml = html.replace(/>([^<]+)</g, (match, p1) => {
      // Decode entities in the text between the tags
      const decodedText = this.replaceHtmlEntities(p1);
      return `>${decodedText}<`;
    });

    return decodedHtml;
  }

  replaceHtmlEntities(text: string): string {
    return text
      .replace(/&nbsp;/g, ' ')    // Replace non-breaking space
      .replace(/&quot;/g, '"')    // Replace double quotes
      .replace(/&apos;/g, "'")     // Replace apostrophes
      .replace(/&lt;/g, '<')       // Replace less than
      .replace(/&gt;/g, '>');      // Replace greater than
  }

  getSharedUsersList(token: string, searchText: string) {
    const headers = new HttpHeaders({
      skip: '',
      Authorization: 'Bearer ' + token
    });

    return this.http.get(`${this.exp_url}/atMentionUsers?size=10&text=${searchText}`, { headers }).pipe(map((res: any) => {
      res.data = res.data.map((item: any, i: number) => {
        return { ...item, id: `${item.id}--${item.email}`, value: `${item.userName}`, "label": `${item.name} (${item.email})` };
      })
      return res.data
    }));
  }

  getSharedDocuments(config: any, text?: string) {
    const headers = new HttpHeaders({
      skip: '',
      Authorization: 'Bearer ' + config.token
    });

    let reqPayload = {
      referralSpaceKey: config?.docSpace?.spaceKey,
      referralSpaceName: config?.docSpace?.spaceName,
      policySpaceKey: config?.docSpace?.spaceKey,
      policySpaceName: config?.docSpace?.spaceName,
      text: text ?? ""
    }

    return this.http.post(`${this.exp_url}/atMentionFiles`, reqPayload, { headers }).pipe(map((response: any) => {
      let data = response?.data?.map((row: any) => {
        row.id = (row.blobId ?? row.id) + '**' + (row.kind?.replace(/ /g, ""));
        return row;
      });
      return data;
    }));
  }

  markAsRead(ids: any[]) {
    return this.http.post(`${this.api_host_url}/alerts/markAsRead`, { ids: ids });
  }

  getOOO(userEmail: string, endDate: string): Observable<any> {
    const reqHeaders: any = {
      'Content-type': 'application/json',
      Accept: 'application/json',
    };

    const url = `${this.api_host_url}/user/ooo/${userEmail}/${endDate}`;
    return this.http.get<any>(url, { headers: new HttpHeaders(reqHeaders) }).pipe(map((response: any) => response.data));
  }

}
